/**
 * FocusTrap – モーダルやパネル内にキーボードフォーカスを閉じ込めるユーティリティ
 *
 * 使い方:
 *   const trap = new FocusTrap(containerEl);
 *   trap.activate();   // フォーカスをコンテナ内に閉じ込める
 *   trap.deactivate(); // 閉じ込めを解除し、元のフォーカスに戻す
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export class FocusTrap {
  constructor(container) {
    /** @type {HTMLElement} */
    this.container = container;
    /** @type {HTMLElement|null} 起動前にフォーカスされていた要素 */
    this.previousFocus = null;
    /** @type {boolean} */
    this.active = false;

    this._onKeyDown = this._onKeyDown.bind(this);
  }

  /** コンテナ内のフォーカス可能要素を取得（表示中のもののみ） */
  _getFocusable() {
    return [...this.container.querySelectorAll(FOCUSABLE)]
      .filter((el) => el.offsetParent !== null);
  }

  /** Tab / Shift+Tab をコンテナ内で巡回させる */
  _onKeyDown(e) {
    if (e.key !== 'Tab') return;

    const focusable = this._getFocusable();
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    // コンテナ自身にフォーカスがある場合（activate 直後の初期状態）
    // Tab なら先頭、Shift+Tab なら末尾の要素へ移動させる
    if (document.activeElement === this.container) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }

    if (e.shiftKey) {
      // Shift+Tab: 先頭にいたら末尾へ
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab: 末尾にいたら先頭へ
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /** フォーカストラップを起動する */
  activate() {
    if (this.active) return;
    this.active = true;

    // 起動前のフォーカスを保存
    this.previousFocus = /** @type {HTMLElement} */ (document.activeElement);

    document.addEventListener('keydown', this._onKeyDown);

    // a11y: ダイアログコンテナ自体にフォーカスを当てる（WAI-ARIA ダイアログパターン）。
    // 最初のフォーカス可能要素（リンク等）に .focus() を呼ぶと、
    // iOS Safari/Chrome で青いフォーカスリングが表示されてしまうため、
    // 代わりにコンテナへフォーカスを移動する。
    // Tab/Shift+Tab の閉じ込めは _onKeyDown で処理するので問題ない。
    // preventScroll: true でアニメーション中のビューポート移動を防ぐ。
    requestAnimationFrame(() => {
      this.container.setAttribute('tabindex', '-1');
      this.container.focus({ preventScroll: true });
    });
  }

  /** フォーカストラップを解除し、元のフォーカスを復元する */
  deactivate() {
    if (!this.active) return;
    this.active = false;

    document.removeEventListener('keydown', this._onKeyDown);

    // 元のフォーカスを復元
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
    this.previousFocus = null;
  }
}
